import React, { useEffect, useMemo, useRef, useState } from 'react';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    DemeterDataFrequency,
    DemeterFeatureType,
    DemeterMarket,
    DemeterRegion,
    DemeterTableDefinitionCommodityModel,
    DemeterTableDefinitionType,
    DemeterUserStoreType,
    DemeterUserType,
} from '../../../Generated/Raven-Demeter';
import { useApplicationDispatch, useApplicationSelector } from '../../../Redux/ReduxStore';
import {
    selectCommodityTopLevelSelections,
    selectRegionTopLevelSelections,
    setCommodityTopLevelSelection,
    setRegionTopLevelSelection,
} from '../../../Redux/Slices/MenuSelectionSlice';
import { selectUserCurrentMarket, selectUserMarkets, selectUserType } from '../../../Redux/Slices/UserSlice';
import useTableDefinitionsApi from '../../Apis/Hooks/useTableDefinitionsApiHook';
import useTableDefinitionsByCommodityApi from '../../Apis/Hooks/useTableDefinitionsCommodityApiHook';
import useUserStoreApi from '../../Apis/Hooks/useUserStoreApiHook';
import browserService from '../../Services/BrowserService';
import useFeatureFlag from '../../Services/FeatureFlags/useFeatureFlagHook';
import trackingService, { TrackingEvent } from '../../Services/Tracking/TrackingService';
import useRegionCommodityMenuSelection from '../Navigation/Hooks/useRegionCommodityMenuSelectionHook';
import useSearchParameters from '../Navigation/Hooks/useSearchParametersHook';
import styles from './MenuFileSelectors.module.scss';
import MenuSelectorItem, { IMenuItem, IRegionCommodityValues, MenuTypeForSearchParameter } from './MenuSelectorItem';
import MenuSelectorWithFlipWrapper from './MenuSelectorWithFlipWrapper';
import regionCommodityGroupingService from './RegionCommodityGroupingService';

interface IMenuByRegionCommodityProps {
    tableDefinitionType: DemeterTableDefinitionType;
    showMainRegionsAndCommoditiesGroupsOnly?: boolean;
    rightSideOfBreadcrumbsComponent?: JSX.Element;
    track: TrackingEvent;
}

const MenuByRegionCommodity: React.FC<IMenuByRegionCommodityProps> = (props: IMenuByRegionCommodityProps) => {
    // Application hooks and constants.
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const [userStoreValue, updateUserStore] = useUserStoreApi(DemeterUserStoreType.DisplaySettings);
    const enableUserData = useFeatureFlag(DemeterFeatureType.UserData);
    const userMarkets = useApplicationSelector(selectUserMarkets);
    const userSelectedMarket = useApplicationSelector(selectUserCurrentMarket);
    const currentUserType = useApplicationSelector(selectUserType);
    const topLevelRegionSelectionsFromState = useApplicationSelector(selectRegionTopLevelSelections);
    const topLevelCommoditySelectionsFromState = useApplicationSelector(selectCommodityTopLevelSelections);
    const dispatch = useApplicationDispatch();
    const isAdministrator = useMemo(() => currentUserType === DemeterUserType.Administrator, [currentUserType]);
    const stopMenuRedirect = isAdministrator && browserService.getSessionStorageItem('playwright.stopMenuRedirect') === 'true';

    // Menu Data
    const tableDefinitionsByRegion = useTableDefinitionsApi(props.tableDefinitionType, false, DemeterMarket.All);
    const tableDefinitionsByCommodity = useTableDefinitionsByCommodityApi(props.tableDefinitionType, DemeterMarket.All);
    const [topLevelMenuItems, setTopLevelMenuItems] = useState<IMenuItem[]>([]);
    const [subLevelMenuItems, setSubLevelMenuItems] = useState<IMenuItem[]>([]);
    const isUserSelectedItem = useRef<boolean>(false);
    const [breadCrumbs, setBreadCrumbs] = useState<string>();
    const userStoreReference = useRef<Record<string, string | boolean | DemeterTableDefinitionCommodityModel[]> | null>(null);
    const [, setRegionCommodityMenuSelection] = useRegionCommodityMenuSelection();
    const regionCommoditySelectionReference = useRef<IRegionCommodityValues>({
        region: searchParameters.region ?? null,
        subRegion: searchParameters.subRegion ?? null,
        commodity: searchParameters.commodity ?? null,
        extraParameters: searchParameters.extraParameters ?? '',
        dataFrequency: searchParameters.dataFrequency ?? '',
        subCommodity: null,
        topLevelGroup: null,
        topLevelRegion: null,
        isSubSelection: false,
    });

    const handleMenuAndSearchParametersUpdate = (
        topLevelItems: IMenuItem[],
        subLevelItems: IMenuItem[],
        selection: IRegionCommodityValues,
        topMenuType: MenuTypeForSearchParameter,
    ) => {
        if (stopMenuRedirect) {
            return;
        }
        setBreadCrumbs(
            regionCommodityGroupingService.getSelectedBreadCrumbLabel(
                [...topLevelItems, ...subLevelItems],
                topMenuType === MenuTypeForSearchParameter.commodity,
            ),
        );

        setTopLevelMenuItems(topLevelItems);
        setSubLevelMenuItems(subLevelItems);
        regionCommoditySelectionReference.current = { ...selection };
        setRegionCommodityMenuSelection({ ...selection });
        if (isUserSelectedItem.current && topMenuType === MenuTypeForSearchParameter.commodity) {
            dispatch(
                setCommodityTopLevelSelection({
                    tableDefinitionType: props.tableDefinitionType,
                    selections: selection,
                }),
            );

            isUserSelectedItem.current = false;
        }
        if (isUserSelectedItem.current && topMenuType === MenuTypeForSearchParameter.region) {
            dispatch(
                setRegionTopLevelSelection({
                    tableDefinitionType: props.tableDefinitionType,
                    selections: selection,
                }),
            );

            isUserSelectedItem.current = false;
        }

        setSearchParameters({
            region: selection.region ?? searchParameters.region ?? '',
            subRegion: selection.subRegion ?? '',
            commodity: selection.subCommodity ?? selection.commodity ?? searchParameters.commodity,
            extraParameters: selection.extraParameters ?? '',
            dataFrequency: selection.dataFrequency ?? DemeterDataFrequency.Monthly,
            topMenuType,
            selected: searchParameters.selected ?? '',
        });
    };

    const setMenusByRegionOrCommodity = (newSelection: IRegionCommodityValues) => {
        trackingService.track({
            eventCategory: props.track.eventCategory,
            eventAction: `${props.track.eventAction}SelectionChanged`,
            eventValue: {
                region: newSelection.region ?? newSelection.topLevelRegion,
                ...(newSelection.subRegion && { subRegion: newSelection.subRegion }),
                commodity: newSelection.commodity ?? newSelection.topLevelGroup,
                ...(newSelection.subCommodity && { subCommodity: newSelection.subCommodity }),
            },
        });

        if (!tableDefinitionsByRegion || !tableDefinitionsByCommodity || !userStoreReference?.current) {
            return;
        }

        const isCommodity = userStoreReference.current[applicationConstants.MenuSettingsTopLevelKey] === MenuTypeForSearchParameter.commodity;
        const userSelectedCommoditiesOrDefault: DemeterTableDefinitionCommodityModel[] =
            !(userStoreReference?.current[applicationConstants.MenuSettingsSelectedCommoditiesKey] as DemeterTableDefinitionCommodityModel[])?.length ||
            !userStoreReference?.current[applicationConstants.MenuSettingsUserHasSetCommodities]
                ? tableDefinitionsByCommodity?.filter((x) => x.market === userSelectedMarket)
                : (userStoreReference?.current[applicationConstants.MenuSettingsSelectedCommoditiesKey] as DemeterTableDefinitionCommodityModel[])?.filter(
                      (x: DemeterTableDefinitionCommodityModel) => x.market && userMarkets.includes(x.market),
                  );

        if (!userSelectedCommoditiesOrDefault || !userSelectedCommoditiesOrDefault.length) {
            return;
        }

        if (!isCommodity || (isCommodity && !tableDefinitionsByCommodity.length)) {
            const availableRegions = regionCommodityGroupingService
                .filterTableDefinitionsByRegionWithUserSelections(tableDefinitionsByRegion, userSelectedCommoditiesOrDefault, props.tableDefinitionType)
                .filter((x) => enableUserData || x.region !== DemeterRegion.UserData);
            if (!availableRegions.length) {
                return;
            }

            const selectionUpdate = regionCommodityGroupingService.getMenuSelectionByRegion(availableRegions, newSelection);
            const regionMenuItems = regionCommodityGroupingService.getMenuItemsByRegionsAndCommodities(
                availableRegions,
                selectionUpdate,
                props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
            );

            const selectedRegion = availableRegions.find((x) => x.region === selectionUpdate.topLevelRegion);
            if (!selectedRegion) {
                const regionCommoditiesMenuItems = regionCommodityGroupingService.getMenuItemsByRegionCommodities(
                    availableRegions[0].demeterTableDefinitionGroups,
                    selectionUpdate,
                    props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
                );

                handleMenuAndSearchParametersUpdate(regionMenuItems, regionCommoditiesMenuItems, selectionUpdate, MenuTypeForSearchParameter.region);
                return;
            }

            const selectedSubRegion = selectedRegion.subRegions?.find((x) => x === selectionUpdate.subRegion);
            const availableCommodities = selectedRegion.demeterTableDefinitionGroups.filter(
                (x) =>
                    !selectedSubRegion ||
                    (x.commodity && x.subRegions.includes(selectedSubRegion)) ||
                    (!x.commodity && x.demeterTableDefinitions.some((y) => y.subRegions?.includes(selectedSubRegion))),
            );

            const regionCommoditiesMenuItems = regionCommodityGroupingService.getMenuItemsByRegionCommodities(
                availableCommodities,
                selectionUpdate,
                props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
            );

            handleMenuAndSearchParametersUpdate(regionMenuItems, regionCommoditiesMenuItems, selectionUpdate, MenuTypeForSearchParameter.region);
            return;
        }

        const availableCommodities = regionCommodityGroupingService.filterTableDefinitionsByCommodityWithUserSelections(
            tableDefinitionsByCommodity,
            userSelectedCommoditiesOrDefault,
            props.tableDefinitionType,
        );

        if (!availableCommodities.length) {
            return;
        }

        const selectionUpdate = regionCommodityGroupingService.getMenuSelectionByCommodity(availableCommodities, newSelection);
        const commodityMenuItems = regionCommodityGroupingService.getMenuItemsByCommodities(
            availableCommodities,
            selectionUpdate,
            props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
        );
        const selectedCommodity = availableCommodities.find((x) => x.topLevelGroup === selectionUpdate.topLevelGroup);
        if (!selectedCommodity) {
            const commodityRegionsMenuItems = regionCommodityGroupingService.getMenuItemsByRegionsAndCommodities(
                availableCommodities[0].demeterTableDefinitionRegions.filter((x) => enableUserData || x.region !== DemeterRegion.UserData),
                selectionUpdate,
                props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
            );

            handleMenuAndSearchParametersUpdate(commodityMenuItems, commodityRegionsMenuItems, selectionUpdate, MenuTypeForSearchParameter.commodity);
            return;
        }

        const availableRegions =
            selectedCommodity?.demeterTableDefinitionSubCommodities
                .find((x) => !!x.commodity && x.commodity === selectionUpdate.subCommodity && x.topLevelGroup === selectionUpdate.topLevelGroup)
                ?.demeterTableDefinitionRegions.filter((x) => enableUserData || x.region !== DemeterRegion.UserData) ??
            selectedCommodity.demeterTableDefinitionRegions.filter((x) => enableUserData || x.region !== DemeterRegion.UserData);
        const commodityRegionsMenuItems = regionCommodityGroupingService.getMenuItemsByRegionsAndCommodities(
            availableRegions,
            selectionUpdate,
            props.showMainRegionsAndCommoditiesGroupsOnly ?? false,
        );

        handleMenuAndSearchParametersUpdate(commodityMenuItems, commodityRegionsMenuItems, selectionUpdate, MenuTypeForSearchParameter.commodity);
    };

    const handleMenuFlip = () => {
        const menuSettingsTopLevelType =
            searchParameters.topMenuType === MenuTypeForSearchParameter.region ? MenuTypeForSearchParameter.commodity : MenuTypeForSearchParameter.region;

        const previousSelectionsForMenu =
            menuSettingsTopLevelType === MenuTypeForSearchParameter.commodity
                ? topLevelCommoditySelectionsFromState[props.tableDefinitionType]
                : topLevelRegionSelectionsFromState[props.tableDefinitionType];

        const flipRegionCommodityValues: IRegionCommodityValues = {
            region: previousSelectionsForMenu?.region ?? searchParameters.region ?? '',
            subRegion: previousSelectionsForMenu?.subRegion ?? searchParameters.subRegion ?? '',
            commodity: previousSelectionsForMenu?.commodity ?? searchParameters.commodity ?? '',
            extraParameters: previousSelectionsForMenu?.extraParameters ?? searchParameters.extraParameters ?? '',
            dataFrequency: previousSelectionsForMenu?.dataFrequency ?? searchParameters.dataFrequency ?? '',
            subCommodity: previousSelectionsForMenu?.subCommodity ?? regionCommoditySelectionReference.current.subCommodity ?? null,
            topLevelGroup: previousSelectionsForMenu?.topLevelGroup ?? regionCommoditySelectionReference.current.topLevelGroup ?? null,
            topLevelRegion: previousSelectionsForMenu?.topLevelRegion ?? regionCommoditySelectionReference.current.topLevelRegion ?? null,
            isSubSelection:
                previousSelectionsForMenu?.isSubSelection ?? (!!regionCommoditySelectionReference.current.subCommodity || !!searchParameters.subRegion),
        };

        updateUserStore({ ...userStoreValue?.userStore?.value, ...{ [applicationConstants.MenuSettingsTopLevelKey]: menuSettingsTopLevelType } });
        userStoreReference.current = {
            ...userStoreReference.current,
            [applicationConstants.MenuSettingsTopLevelKey]: menuSettingsTopLevelType,
        };

        setMenusByRegionOrCommodity(flipRegionCommodityValues);
    };

    const handleMenuItemSelection = (selection: IMenuItem) => {
        const selectedSubRegion =
            selection.regionCommodityValues.subRegion !== null
                ? selection.regionCommodityValues.subRegion
                : regionCommoditySelectionReference.current.subRegion;

        const selectedSubCommodity =
            selection.regionCommodityValues.subCommodity !== null
                ? selection.regionCommodityValues.subCommodity
                : regionCommoditySelectionReference.current.subCommodity;

        const selectionRegionCommodityValues: IRegionCommodityValues = {
            region: selection.regionCommodityValues.region,
            subRegion: selectedSubRegion,
            commodity: selection.regionCommodityValues.commodity,
            extraParameters: selection.regionCommodityValues.extraParameters,
            dataFrequency: selection.regionCommodityValues.dataFrequency,
            subCommodity: selectedSubCommodity,
            topLevelGroup: selection.regionCommodityValues.topLevelGroup,
            topLevelRegion: selection.regionCommodityValues.topLevelRegion,
            isSubSelection: selection.regionCommodityValues.isSubSelection,
        };

        isUserSelectedItem.current = true;
        setMenusByRegionOrCommodity(selectionRegionCommodityValues);
    };

    useEffect(() => {
        setTopLevelMenuItems([]);
        setSubLevelMenuItems([]);
    }, [props.tableDefinitionType]);

    useEffect(() => {
        if (!tableDefinitionsByRegion?.length || !tableDefinitionsByCommodity?.length) {
            return;
        }

        const isTopMenuTypeCommodity = searchParameters.topMenuType === MenuTypeForSearchParameter.commodity;
        const previousMenuSelectionsFromState = isTopMenuTypeCommodity
            ? topLevelCommoditySelectionsFromState[props.tableDefinitionType]
            : topLevelRegionSelectionsFromState[props.tableDefinitionType];
        const getCommodity = tableDefinitionsByCommodity.find(
            (x) =>
                (x.commodity && x.commodity === searchParameters.commodity) ||
                (!x.commodity && x.demeterTableDefinitionSubCommodities.some((y) => y.commodity === searchParameters.commodity)) ||
                x.demeterTableDefinitionRegions.some((y) => y.commodity === searchParameters.commodity),
        );
        const getCommodityByRegion = tableDefinitionsByRegion
            .find((x) => x.region === searchParameters.region)
            ?.demeterTableDefinitionGroups.find(
                (x) =>
                    (x.commodity && x.commodity === searchParameters.commodity) ||
                    (!x.commodity && x.demeterTableDefinitions.some((y) => y.commodity === searchParameters.commodity)),
            );

        const topLevelGroup = isTopMenuTypeCommodity ? getCommodity?.topLevelGroup : getCommodityByRegion?.topLevelGroup;

        const checkIfCommodityExtraParametersIsSubCommodity =
            getCommodity?.demeterTableDefinitionRegions.find(
                (x) =>
                    searchParameters.extraParameters &&
                    x.extraParameters &&
                    x.region === searchParameters.region &&
                    x.extraParameters === searchParameters.extraParameters,
            ) ??
            getCommodity?.demeterTableDefinitionSubCommodities.find(
                (x) =>
                    searchParameters.extraParameters &&
                    x.topLevelGroup === topLevelGroup &&
                    x.demeterTableDefinitionRegions.some((y) => y.region === searchParameters.region && y.extraParameters === searchParameters.extraParameters),
            );
        const checkIfCommodityByRegionExtraParametersIsSubCommodity = getCommodityByRegion?.demeterTableDefinitions.find(
            (x) => searchParameters.extraParameters && x.extraParameters === searchParameters.extraParameters,
        );

        const checkIfCommodityIsSubCommodity =
            !getCommodity?.commodity || (getCommodity?.commodity && getCommodity.commodity !== searchParameters.commodity)
                ? getCommodity?.demeterTableDefinitionSubCommodities.find((x) => x.commodity === searchParameters.commodity)?.commodity
                : null;
        const checkIfCommodityByRegionIsSubCommodity =
            !getCommodityByRegion?.commodity || (getCommodityByRegion?.commodity && getCommodityByRegion.commodity !== searchParameters.commodity)
                ? getCommodityByRegion?.demeterTableDefinitions.find((x) => x.commodity === searchParameters.commodity)?.commodity
                : null;
        const subCommodityValue = isTopMenuTypeCommodity ? checkIfCommodityIsSubCommodity ?? null : checkIfCommodityByRegionIsSubCommodity ?? null;
        const subCommodityByExtraParameters = isTopMenuTypeCommodity
            ? checkIfCommodityExtraParametersIsSubCommodity?.commodity ?? null
            : checkIfCommodityByRegionExtraParametersIsSubCommodity?.commodity ?? null;

        const regionCommodityInitialValues: IRegionCommodityValues = {
            region: previousMenuSelectionsFromState?.region ?? searchParameters.region ?? null,
            subRegion: previousMenuSelectionsFromState?.subRegion ?? searchParameters.subRegion ?? null,
            commodity: previousMenuSelectionsFromState?.commodity ?? searchParameters.commodity ?? null,
            extraParameters: previousMenuSelectionsFromState?.extraParameters ?? searchParameters.extraParameters ?? '',
            dataFrequency: previousMenuSelectionsFromState?.dataFrequency ?? searchParameters.dataFrequency ?? '',
            subCommodity: previousMenuSelectionsFromState?.subCommodity ?? subCommodityValue ?? subCommodityByExtraParameters,
            topLevelRegion: previousMenuSelectionsFromState?.topLevelRegion ?? searchParameters.region ?? null,
            topLevelGroup:
                previousMenuSelectionsFromState?.topLevelGroup ??
                topLevelGroup ??
                tableDefinitionsByCommodity.find((x) => x.commodity === searchParameters.commodity)?.topLevelGroup ??
                null,
            isSubSelection: previousMenuSelectionsFromState?.isSubSelection ?? false,
        };

        regionCommoditySelectionReference.current = { ...regionCommodityInitialValues };
        setRegionCommodityMenuSelection({ ...regionCommodityInitialValues });
        setMenusByRegionOrCommodity(regionCommodityInitialValues);
    }, [tableDefinitionsByRegion, tableDefinitionsByCommodity]);

    useEffect(() => {
        if (userStoreReference.current || !userStoreValue) {
            return;
        }

        const userHasValidSelectedCommodities =
            userStoreValue?.userStore?.value[applicationConstants.MenuSettingsSelectedCommoditiesKey]?.length &&
            userStoreValue?.userStore?.value[applicationConstants.MenuSettingsUserHasSetCommodities];

        userStoreReference.current = {
            [applicationConstants.MenuSettingsUserAlreadyAcknowledged]: true,
            [applicationConstants.MenuSettingsTopLevelKey]:
                userStoreValue?.userStore?.value[applicationConstants.MenuSettingsTopLevelKey] ??
                searchParameters.topMenuType ??
                MenuTypeForSearchParameter.region,
            [applicationConstants.MenuSettingsUserHasSetCommodities]: !userHasValidSelectedCommodities
                ? false
                : userStoreValue?.userStore?.value[applicationConstants.MenuSettingsUserHasSetCommodities],
            [applicationConstants.MenuSettingsSelectedCommoditiesKey]: !userHasValidSelectedCommodities
                ? tableDefinitionsByCommodity?.filter((x) => x.market === userSelectedMarket)
                : userStoreValue?.userStore?.value[applicationConstants.MenuSettingsSelectedCommoditiesKey]?.filter(
                      (x: DemeterTableDefinitionCommodityModel) => x.tableDefinitionType === DemeterTableDefinitionType.AllFundamentalsAndPrices,
                  ),
        };
    }, [userStoreValue, regionCommoditySelectionReference.current]);

    const handleMenuItemsUpdate = (settings: Record<string, string | boolean | DemeterTableDefinitionCommodityModel[]>) => {
        const previousMenuSelectionsFromState =
            settings.topMenuType === MenuTypeForSearchParameter.commodity
                ? topLevelCommoditySelectionsFromState[props.tableDefinitionType]
                : topLevelRegionSelectionsFromState[props.tableDefinitionType];

        userStoreReference.current = { ...settings };
        updateUserStore({ ...settings });
        setMenusByRegionOrCommodity(previousMenuSelectionsFromState ?? regionCommoditySelectionReference.current);
    };

    const isMenuLoading = topLevelMenuItems.length === 0 || subLevelMenuItems.length === 0;

    return (
        <MenuSelectorWithFlipWrapper
            isLoading={isMenuLoading}
            rightSideOfBreadcrumbsComponent={isMenuLoading ? undefined : props.rightSideOfBreadcrumbsComponent}
            breadCrumbs={breadCrumbs}
            tableDefinitionType={props.tableDefinitionType}
            handleMenuFlip={handleMenuFlip}
            handleMenuUpdate={handleMenuItemsUpdate}
            track={{ eventCategory: props.track.eventCategory, eventAction: 'MenuByRegionCommodity' }}
        >
            {isMenuLoading ? (
                <div className={styles.menu_loading_bar} />
            ) : (
                <div>
                    <div className={styles.menu_with_flip_topMenu}>
                        {topLevelMenuItems.map((menuItem: IMenuItem) => (
                            <div key={menuItem.id} className={menuItem.isSelectedItem ? styles.menu_with_flip_topItem_selected : styles.menu_with_flip_topItem}>
                                <MenuSelectorItem
                                    menuType="topItem"
                                    menuItem={menuItem}
                                    classname={menuItem.isSelectedItem ? styles.menu_with_flip_topItem_button_selected : styles.menu_with_flip_topItem_button}
                                    buttonClassname={styles.menu_with_flip_topItem_button}
                                    labelClassname={styles.menu_with_flip_topItem_button_label}
                                    simpleClassname={styles.menu_with_flip_topItem_button_simple}
                                    selectorClassname={styles.menu_with_flip_topItem_button_selector}
                                    dropdownClassname={styles.menu_with_flip_topItem_button_dropdown}
                                    dividerClassName={
                                        menuItem.isSelectedItem ? styles.menu_with_flip_topItem_selected_divider : styles.menu_with_flip_topItem_divider
                                    }
                                    isSelected={menuItem.isSelectedItem}
                                    handleMenuSelection={handleMenuItemSelection}
                                    showMainRegionsAndGroupsOnly={props.showMainRegionsAndCommoditiesGroupsOnly}
                                />
                            </div>
                        ))}
                    </div>

                    <div className={styles.menu_with_flip_subMenu}>
                        {subLevelMenuItems.map((subMenuItem: IMenuItem) => (
                            <div
                                key={subMenuItem.id}
                                className={subMenuItem.isSelectedItem ? styles.menu_with_flip_subItem_selected : styles.menu_with_flip_subItem}
                            >
                                <MenuSelectorItem
                                    menuType="subItem"
                                    menuItem={subMenuItem}
                                    classname={
                                        subMenuItem.isSelectedItem ? styles.menu_with_flip_subItem_button_selected : styles.menu_with_flip_subItem_button
                                    }
                                    buttonClassname={styles.menu_with_flip_subItem_button}
                                    labelClassname={styles.menu_with_flip_subItem_button_label}
                                    simpleClassname={styles.menu_with_flip_subItem_button_simple}
                                    selectorClassname={styles.menu_with_flip_subItem_simple_selector}
                                    dropdownClassname={styles.menu_with_flip_subItem_button_dropdown}
                                    dividerClassName={
                                        subMenuItem.isSelectedItem ? styles.menu_with_flip_subItem_divider_selected : styles.menu_with_flip_subItem_divider
                                    }
                                    isSelected={subMenuItem.isSelectedItem}
                                    handleMenuSelection={handleMenuItemSelection}
                                    showMainRegionsAndGroupsOnly={props.showMainRegionsAndCommoditiesGroupsOnly}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </MenuSelectorWithFlipWrapper>
    );
};

export default MenuByRegionCommodity;
